<template>
	<div class="grid grid-cols-1 lg:grid-cols-2 gap-4" :title="ppcData.name">
		<!-- Landing Image -->
		<div class="landing-image-width row-start-1 col-span-full">
			<img loading="lazy" class="landing-background-image" :src="`${urlLocation + ppcData.title_image}`" />
		</div>

		<!-- Main Heading -->
		<div class="transleaf row-start-1 col-start-1 place-self-center place-content-center text-white  md:mt-1 px-8"
			style="background: linear-gradient(90deg, #00810E 0%, rgba(0, 129, 14, 0.3) 100%); height: 320px;max-width: 550px;">
			<div class="text-2xl sm:text-4xl leading-[40px] pb-4 font-semibold">
				{{ ppcData.main_heading }}
			</div>
			<div class="text-md sm:text-lg py-2 font-semibold" style="font-size: 18px;">
				<span> {{ processedDescription }} </span>
			</div>
			<div style="font-size: 14px;font-weight: 400;" v-if="isPremiumPackage">
				{{ ppcData.marketing_block_10_copy }}
			</div>
		</div>

		<!-- Quote Form -->
		<div class="quote lg:row-start-1 lg:row-span-2 lg:col-start-2 place-self-center" id="personalizedQuoteForm">
			<h3>{{ ppcData.contact_form_heading }}</h3>
			<p class="text-center">{{ ppcData.contact_form_helper_text }}</p>
			<div class="quoteform mx-auto" ref="formSection">
				<form>
					<!-- Validation Errors -->
					<div v-if="errors.length" class="red">
						<b>Please correct the following error(s):</b>
						<ul>
							<li v-for="(error, index) in errors" :key="index" class="validation-error">
								{{ error }}
							</li>
						</ul>
					</div>

					<!-- Input Fields -->
					<div class="grid grid-cols-2 gap-4">
						<input type="text" v-model="firstName" name="firstname" placeholder="First Name *"
							class="w-full" required />
						<input type="text" v-model="lastName" name="lastname" placeholder="Last Name *" class="w-full"
							required />
					</div>
					<div class="grid grid-cols-2 gap-4">
						<input type="email" v-model="emailAddress" name="email" placeholder="Email Address *"
							class="w-full" required />
						<MaskInput type="text" :mask="PHONE_INPUT_MASK" placeholder="Phone Number *" autocomplete="tel"
							name="pnumber" v-model="pnumber" :value="pnumber" class="w-full" required />
					</div>
					<div class="relative md:w-full">
						<input type="text" v-model="address" name="address" placeholder="Street Address *"
							class="w-full" @keyup="smartystreets" required />
						<ul ref="smartyStreetsAutocomplete" style="display: none">
							<li v-for="(result, index) in results" :key="index" @click="selectAddress(result)">
								{{ result.street_line }}, {{ result.city }},
								{{ result.state }}, {{ result.zipcode }}
							</li>
						</ul>
					</div>
					<div class="relative md:w-full">
						<input type="text" v-model="city" name="city" placeholder="City *" class="w-full" disabled
							required />
					</div>
					<div class="grid grid-cols-2 gap-4">
						<input type="text" v-model="state" name="state" placeholder="State *" disabled required />
						<input type="text" v-model="zipcode" name="zip" placeholder="Zipcode *" maxlength="5" disabled
							required />
					</div>
					<textarea v-model="comments" name="comments" placeholder="Comments" class="w-full"></textarea>

					<!-- Checkbox -->
					<div class="flex justify-center md:justify-start">
						<label class="checkbox" for="allowText">
							<span class="checkbox__input">
								<input type="checkbox" id="allowText" name="allowText" v-model="allowText" />
								<span class="checkbox__control">
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
										<path fill="none" stroke="currentColor" stroke-width="3"
											d="M1.73 12.91l6.37 6.37L22.79 4.59" />
									</svg>
								</span>
							</span>
							<p class="text-sm my-2">
							  By checking this box, you agree to receive text messages (e.g. promotions, service notifications/reminders)
							  from Spring Green at the number provided and consent to the monitoring and storage of the conversation.
							  Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies.
							  Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available).
							  HELP for assistance. See our
							  <a href="/policies/privacy-policy" target="_blank">Privacy Policy</a> and
							  our <a href="/policies/terms-use" target="_blank">Terms of Use</a>.
							</p>
						</label>
					</div>

					<!-- Submit Button -->
					<button type="submit" class="w-full mt-4 py-3" :disabled="isFormSubmitProcessing"
						@click.prevent="formSubmit()">
						Submit
					</button>

				</form>
				<p class="text-center mt-4">
					{{ ppcData.contact_form_ndc_teaser_question }}<br />
					<a href=""> {{ ppcData.contact_form_ndc_teaser_cta }}</a>
				</p>
			</div>
		</div>

		<!-- Main Content -->
		<div class="text lg:row-start-2 lg:row-span-1 lg:col-start-1">
			<div class="mx-8 main-description styled-html" v-html="mainDescription"> </div>
		</div>

		<!-- Video Section -->
		<div class="lg:row-start-3 lg:col-start-2 mx-auto hidden md:block" v-if="youtubeLink">
			<iframe width="560" height="315" :src="youtubeLink" title="YouTube video player" frameborder="0"
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowfullscreen></iframe>
		</div>
	</div>
</template>

<script setup lang="ts">
import _ from "lodash";
import { useStore } from "vuex";
import type { Store } from "vuex/types/index.js";
import { MaskInput } from "vue-3-mask";
import {
	formatPhoneNumber,
	getCustomerPhoneNumber,
	getPhoneParts,
	PHONE_INPUT_MASK,
} from "~/utils/phone";
import type State from "~/domains/store/State";
import type { SmartyStreetsLookupResult } from "~/domains/smartystreets/SmartyStreetsLookupResult";
import type { SmartyStreetsLookupSuggestion } from "~/domains/smartystreets/SmartyStreetsLookupSuggestion";
import type { PpcContactForm } from "~/domains/ppc/PpcContactForm";

const { $config, $st } = useNuxtApp();
const store: Store<State> = useStore();

const props = defineProps({
	ppcData: {
		type: Object,
		required: true,
	},
	errors: {
		type: Array<string>,
		default: () => [],
	},
	isFormSubmitProcessing: {
		type: Boolean,
		default: false,
	},
	isPremiumPackage: {
		type: Boolean,
		default: false,
	},
	currentPackage: {
		type: [Number, Object],
		default: null,
	},
});

const emits = defineEmits(["submit", "scroll-to-form"]);

const firstName = ref("");
const lastName = ref("");
const emailAddress = ref("");
const address = ref("");
const city = ref("");
const state = ref("");
const zipcode = ref("");
const pnumber = ref("");
const allowText = ref(false);
const comments = ref("");
const results = ref<SmartyStreetsLookupSuggestion[]>([]);
const urlLocation = ref($config.public.sgCmsImage);
const youtubeLink = ref("");
const userAddress = ref("");

const branchInfo = computed(() => {
	return store.state.persistedState.branchInfo;
});

const premiumPackage = computed(() => {
	return branchInfo.value?.UPPackage?.find(
		(item) =>  item.Year === branchInfo.value.IMCYear && props.currentPackage === item.PNameID
	);
});

const mainDescription = computed(() => {
	if (props.isPremiumPackage && premiumPackage.value?.PackageFODesc) {
		return `<h2><strong>Experience The Highest Level Of Lawn Care</strong></h2> <p> ${premiumPackage.value.PackageFODesc} </p>`;
	}

	return props.ppcData?.description;
});

const couponServiceName = computed(() => {
	const primaryOffer = premiumPackage.value?.["Programs and Services"]?.find(
		(service) => !!service.PrimaryOffer
	);
	return primaryOffer?.['Programs / Services'];
});

const processedDescription = computed(() => {
	return props.ppcData?.main_heading_subtext?.replace("{{default_offer}}", couponServiceName.value || "amazing offer") ?? "";
});

watch(
	() => store.state.persistedState,
	(newValue) => {
		autoPopulateForm();
	},
	{ immediate: false, deep: true }
);

const smartyStreetsAutocomplete = ref<HTMLElement | null>(null);

onMounted(() => {
	const persistedState = store.state.persistedState;
	userAddress.value = persistedState.userAddress;

	if (props.ppcData?.ppc_youtube_video_shortcode) {
		youtubeLink.value = `https://www.youtube.com/embed/${props.ppcData?.ppc_youtube_video_shortcode}`;
	}

	autoPopulateForm();
});

async function autoPopulateForm() {
	// If persisted customer data exists, auto populate the form fields.
	const customerData = store.state.persistedState.customer;

	if (customerData) {
		const addr = customerData.streetNo + " " + customerData.streetNm + " " + customerData.suffix;

		firstName.value = customerData.firstName?.trim() ?? "";
		lastName.value = customerData.lastName?.trim() ?? "";
		emailAddress.value = (customerData.preferredEmail ?? customerData.email)?.trim() ?? "";
		const customerPhone =
			customerData.preferredPhone?.number ??
			getCustomerPhoneNumber(customerData, "Home") ??
			getCustomerPhoneNumber(customerData, "Cell");
		pnumber.value = formatPhoneNumber(customerPhone) ?? "";
		address.value = (customerData.address?.addressLine1 ?? addr ?? customerData.address1)?.trim();
		city.value = (customerData.address?.city ?? customerData.city)?.trim() ?? "";
		state.value = (customerData.address?.state ?? customerData.state)?.trim() ?? "";
		zipcode.value = (customerData.address?.zip ?? customerData.zip)?.trim().substring(0, 5) ?? "";
	}
};

const smartystreets = _.debounce(async () => {
	if (address.value.length > 0) {
		const lookupResults: SmartyStreetsLookupResult = await $st.lookup(address.value);
		if (Object.keys(lookupResults).length > 0) {
			results.value = lookupResults.suggestions;
			smartyStreetsAutocomplete.value.style.display = "block";
		}
	} else {
		smartyStreetsAutocomplete.value.style.display = "none";
	}
}, 300);

const selectAddress = (element: SmartyStreetsLookupSuggestion) => {
	address.value = element.street_line;
	city.value = element.city;
	state.value = element.state;
	zipcode.value = element.zipcode;
	smartyStreetsAutocomplete.value.style.display = "none";
};

const formSubmit = () => {
	const phoneParts = getPhoneParts(pnumber.value);

	const formData: PpcContactForm = {
		firstName: firstName.value,
		lastName: lastName.value,
		emailAddress: emailAddress.value,
		address: address.value,
		city: city.value.trim(),
		state: state.value,
		zipcode: zipcode.value,
		pnumber: pnumber.value,
		areaCode: phoneParts?.areaCode ?? "",
		phonePrefix: phoneParts?.phonePrefix ?? "",
		phoneNumber: phoneParts?.phoneNumber ?? "",
		allowText: allowText.value,
		comments: comments.value,
	};

	emits("submit", formData);
};
</script>

<style scoped>
.landing-image-width img {
	width: 100%;
}

.landing-background-image {
	background-position: left -71px;
	/* Center the image */
	background-repeat: no-repeat;
	/* Do not repeat the image */
	background-size: 1440px 641px;
	/* Resize the background image to cover the entire container */
	height: 500px;
	border-bottom-left-radius: 70px;
}

.transleaf {
	max-height: 291px;
	max-width: 506px;
	background: linear-gradient(180deg,
			rgba(91, 179, 94, 0.309412) 0%,
			#5bb35e 100%);
	border-bottom-right-radius: 99px;
	border-top-left-radius: 99px;
	margin: 4em;
}

.transleaf h2 {
	font-style: normal;
	font-weight: 600;
	font-size: 36px;
	line-height: 40px;
}

.quote {
	background: #f7f7f7;
	border-radius: 36px;
	margin: 4em;
	padding: 1em 2em;
	margin-bottom: 0;
	padding-bottom: 0;
}

.quote h3 {
	font-style: normal;
	font-weight: 600;
	font-size: 26px;
	line-height: 32px;
	text-align: center;
	color: #517185;
	margin-top: 1rem;
}

.quote p {
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 22px;
	color: #517185;
}

.text h2 {
	font-family: Poppins;
	font-style: normal;
	font-weight: 600;
	font-size: 26px;
	line-height: 32px;
	color: #517185;
	font-display: swap;
}

.text h3 {
	font-style: normal;
	font-weight: 600;
	font-size: 18px;
	line-height: 22px;
	color: #517185;
}

.bg-gray-200 {
	background-color: #eef1f4;
}

h4 {
	font-family: Poppins;
	font-style: normal;
	font-weight: 600;
	font-size: 18px;
	line-height: 22px;
	text-align: center;
	color: #00810e;
	font-display: swap;
}

button {
	background: #00810e;
	border-radius: 100px;
	font-family: Poppins;
	font-style: normal;
	font-weight: bold;
	font-size: 18px;
	line-height: 22px;
	text-align: center;
	color: #ffffff;
	font-display: swap;
}

.reviews-inner {
	background: #fff;
	border-bottom-left-radius: 70px;
	padding: 0 0 40px;
}

@media (max-width: 1120px) {
	.transleaf {
		min-height: 220px;
		margin: 2em;
	}

	.quote {
		margin: 0.5em 1em;
	}
}

@media (max-width: 767px) {
	.transleaf h2 {
		font-size: 30px;
		line-height: 1.2;
		margin-top: 30px;
	}
}

.validation-error {
	color: #CE4200;
}

.checkbox {
	display: grid;
	grid-template-columns: min-content auto;
	grid-gap: 0.5em;
}

.checkbox__control {
	display: inline-grid;
	width: 1em;
	height: 1em;
	border-radius: 0.25em;
	border: 0.1em solid #00810e;
	background: #fff;
	color: white;
	margin-top: -3px;
}

.checkbox__control svg {
	transition: transform 0.1s ease-in 25ms;
	transform: scale(0);
	transform-origin: bottom left;
}

.checkbox__input {
	display: grid;
	grid-template-areas: "checkbox";
}

.checkbox__input input {
	opacity: 0;
	width: 0;
	height: 0;
}

.checkbox__input input:checked+.checkbox__control svg {
	transform: scale(1);
}

.checkbox__input input:checked+.checkbox__control {
	background: #00810e;
}
</style>
